import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Calendar, MapPin, Clock, User, Mail, Phone, MessageSquare, Check, DollarSign, Shield, Plus, X, Wifi } from 'lucide-react';
import { zones, calculateFare, SHOPPING_STOP_OPTIONS } from '../mock/fareData';
import { createBooking } from '../utils/api';
import PayPalCheckout from '../components/PayPalCheckout';
import { formatTime12Hour } from '../utils/timeFormat';
import SEOHead from '../components/SEOHead';

const BookingForm = () => {
  const navigate = useNavigate();
  const [additionalStops, setAdditionalStops] = useState([]);

  // Scroll to top on mount so mobile users landing here from a scrolled
  // Home page see the start of the booking form, not the middle/bottom.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    passengerName: '',
    passengerEmail: '',
    passengerPhone: '',
    pickupZone: 'lpia',
    dropoffZone: '',
    date: '',
    time: '',
    passengers: 2,
    bags: false,
    luggage: 0,
    largeBags: 0,
    childrenUnder3: 0,
    carSeats: 0,
    waitingMinutes: 0,
    specialRequests: '',
    paymentMethod: 'Cash',
    // Shopping Plaza Stop — single-select. Values: none | grocery | liquor | both
    shoppingStop: 'none',
    // Flight details
    airline: '',
    flightNumber: '',
    departureTime: '',
    arrivalTime: ''
  });
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [paypalShown, setPaypalShown] = useState(false);
  const [paypalPaidInfo, setPaypalPaidInfo] = useState(null);
  const [fareEstimate, setFareEstimate] = useState(null);

  // 2026-06-26 (LB-3): Same-zone guard. Pickup === Drop-off must be impossible
  // to submit and must never surface a fallback fare to the customer.
  const isSameZone = Boolean(
    formData.pickupZone &&
    formData.dropoffZone &&
    formData.pickupZone === formData.dropoffZone
  );
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef(null);

  // Calculate fare whenever relevant fields change
  useEffect(() => {
    if (formData.pickupZone && formData.dropoffZone && formData.pickupZone !== formData.dropoffZone) {
      const breakdown = calculateFare(
        formData.pickupZone,
        formData.dropoffZone,
        formData.passengers,
        formData.luggage,
        formData.largeBags,
        formData.childrenUnder3,
        formData.carSeats,
        formData.waitingMinutes,
        additionalStops,
        formData.shoppingStop
      );
      setFareEstimate(breakdown);
    } else {
      setFareEstimate(null);
    }
  }, [formData.pickupZone, formData.dropoffZone, formData.passengers, formData.luggage, formData.largeBags, formData.childrenUnder3, formData.carSeats, formData.waitingMinutes, formData.shoppingStop, additionalStops]);

  const addStop = () => {
    if (additionalStops.length < 3) {
      setAdditionalStops([...additionalStops, '']);
    }
  };

  const removeStop = (index) => {
    setAdditionalStops(additionalStops.filter((_, i) => i !== index));
  };

  const updateStop = (index, value) => {
    const newStops = [...additionalStops];
    newStops[index] = value;
    setAdditionalStops(newStops);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const finalValue = type === 'number' ? (parseInt(value) || 0) : value;
    setFormData({
      ...formData,
      [name]: finalValue,
      // Keep legacy `bags` boolean in sync with the suitcase count so the
      // backend record (which still has bags:bool) reflects whether any
      // luggage was declared. No other behavior change.
      ...(name === 'luggage' ? { bags: finalValue > 0 } : {}),
    });
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
    setCaptchaError('Your security check expired. Please complete the verification below again before submitting.');
    if (captchaRef.current) {
      try { captchaRef.current.resetCaptcha(); } catch (_) {}
    }
  };

  const handleCaptchaError = (err) => {
    setCaptchaError('The security check failed to load or returned an error. Please try again.');
    setCaptchaToken(null);
    if (captchaRef.current) {
      try { captchaRef.current.resetCaptcha(); } catch (_) {}
    }
  };

  // Translate backend errors into a user-facing message + per-field errors.
  const FIELD_LABELS = {
    passengerName: 'Full Name',
    passengerEmail: 'Email Address',
    passengerPhone: 'Phone Number',
    pickupZone: 'Pickup Zone',
    dropoffZone: 'Drop-off Zone',
    pickupLocation: 'Pickup Location',
    dropoffLocation: 'Drop-off Location',
    date: 'Date',
    time: 'Time',
    passengers: 'Passengers',
    luggage: 'Luggage',
    largeBags: 'Large Bags',
    childrenUnder3: 'Kids Under 3',
    carSeats: 'Car Seats',
    waitingMinutes: 'Waiting Time',
    specialRequests: 'Additional Notes',
    estimatedFare: 'Fare Estimate',
    hCaptchaToken: 'Security Verification',
  };

  const describeBookingError = (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data;

    // 403 from hCaptcha backend verification
    if (status === 403) {
      return {
        message: 'Security verification failed. Please complete the hCaptcha check again and resubmit.',
        fieldErrors: {},
        resetCaptcha: true,
      };
    }

    // 400 missing captcha token
    if (status === 400) {
      const detail = typeof data?.detail === 'string' ? data.detail : '';
      if (/captcha|verification/i.test(detail)) {
        return {
          message: detail,
          fieldErrors: {},
          resetCaptcha: true,
        };
      }
      return { message: detail || 'Bad request. Please check your details and try again.', fieldErrors: {} };
    }

    // 422 Pydantic validation errors
    if (status === 422 && Array.isArray(data?.detail)) {
      const fe = {};
      const bullets = [];
      for (const e of data.detail) {
        const loc = Array.isArray(e.loc) ? e.loc : [];
        const leaf = String(loc[loc.length - 1] || '');
        const label = FIELD_LABELS[leaf] || leaf || 'Field';
        fe[leaf] = e.msg;
        bullets.push(`• ${label}: ${e.msg}`);
      }
      return {
        message: 'Some of your details are invalid. Please review the highlighted fields:\n' + bullets.join('\n'),
        fieldErrors: fe,
      };
    }

    // 429 rate limit
    if (status === 429) {
      const detail = typeof data?.detail === 'string' ? data.detail : 'Too many booking attempts. Please wait a moment and try again.';
      return { message: detail, fieldErrors: {} };
    }

    // Generic/network
    if (err?.message && /network/i.test(err.message)) {
      return { message: 'Network error. Please check your connection and try again.', fieldErrors: {} };
    }

    const detail = typeof data?.detail === 'string' ? data.detail : null;
    return { message: detail || 'Failed to create booking. Please try again or contact support.', fieldErrors: {} };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setFieldErrors({});

    // Validate CAPTCHA
    if (!captchaToken) {
      setCaptchaError('Please complete the security verification below before submitting.');
      setSubmitError('Security verification is required to book a ride.');
      return;
    }

    // Validate zones
    if (!formData.pickupZone || !formData.dropoffZone) {
      setSubmitError('Please select both pickup and drop-off zones.');
      return;
    }

    if (formData.pickupZone === formData.dropoffZone) {
      setSubmitError('Pickup and drop-off zones must be different.');
      return;
    }

    // Check if one zone is LPIA
    if (formData.pickupZone !== 'lpia' && formData.dropoffZone !== 'lpia') {
      setSubmitError('Bookings must be from or to LPIA Airport.');
      return;
    }

    setSubmitting(true);
    try {
      // Get zone names
      const pickupZoneName = zones.find(z => z.id === formData.pickupZone)?.name || formData.pickupZone;
      const dropoffZoneName = zones.find(z => z.id === formData.dropoffZone)?.name || formData.dropoffZone;

      // Create booking via API
      const bookingPayload = {
        ...formData,
        pickupLocation: pickupZoneName,
        dropoffLocation: dropoffZoneName,
        additionalStops: additionalStops.filter(stop => stop && stop.trim() !== '').map(stopId => zones.find(z => z.id === stopId)?.name || stopId),
        estimatedFare: fareEstimate?.total || 0,
        hCaptchaToken: captchaToken
      };

      const newBooking = await createBooking(bookingPayload);
      setBookingId(newBooking.id);
      setBookingConfirmed(true);
      setPaypalShown(false);
      setPaypalPaidInfo(null);

      // Reset captcha
      setCaptchaToken(null);
      if (captchaRef.current) {
        try { captchaRef.current.resetCaptcha(); } catch (_) {}
      }
    } catch (error) {
      console.error('Booking failed:', error);
      const { message, fieldErrors: fe, resetCaptcha } = describeBookingError(error);
      setSubmitError(message);
      setFieldErrors(fe || {});
      if (resetCaptcha) {
        setCaptchaToken(null);
        setCaptchaError('Please complete the security verification again.');
        if (captchaRef.current) {
          try { captchaRef.current.resetCaptcha(); } catch (_) {}
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-12 rounded-3xl border border-green-200 text-center shadow-xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-400">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Booking Scheduled!</h2>
            <p className="text-xl text-gray-700 mb-6">
              Your booking has been received. Taxi-98 will contact you to confirm your ride.
            </p>
            <div className="bg-cyan-50 p-6 rounded-xl mb-8 border border-cyan-200">
              <p className="text-gray-600 mb-2">Booking ID:</p>
              <p className="text-2xl font-bold text-cyan-600">{bookingId}</p>
            </div>
            {fareEstimate && (
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white p-6 rounded-xl mb-8">
                <p className="text-cyan-100 mb-2">Estimated Fare</p>
                <p className="text-4xl font-bold">${fareEstimate.total.toFixed(2)}</p>
              </div>
            )}

            {/* Optional online payment via PayPal (cash always available — no pressure) */}
            {fareEstimate && fareEstimate.total > 0 && (
              <div
                data-testid="booking-payment-panel"
                className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8 text-left"
              >
                <p className="text-sm font-semibold text-gray-900 mb-1">Payment</p>
                <p className="text-sm text-gray-600 mb-3">
                  Pay later in cash on arrival, or pay now with PayPal — either works.
                </p>
                {paypalPaidInfo ? (
                  <PayPalCheckout
                    bookingId={bookingId}
                    amount={fareEstimate.total}
                    onPaid={(info) => setPaypalPaidInfo(info)}
                  />
                ) : !paypalShown ? (
                  <button
                    type="button"
                    data-testid="show-paypal-btn"
                    onClick={() => setPaypalShown(true)}
                    className="bg-yellow-400 hover:bg-yellow-500 text-black px-5 py-3 rounded-lg font-bold text-base inline-flex items-center gap-2 border-2 border-black/10 transition-colors"
                  >
                    Pay ${fareEstimate.total.toFixed(2)} with PayPal
                  </button>
                ) : (
                  <PayPalCheckout
                    bookingId={bookingId}
                    amount={fareEstimate.total}
                    onPaid={(info) => setPaypalPaidInfo(info)}
                  />
                )}
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
              <p className="text-gray-700 text-sm">
                <strong className="text-gray-900">Next steps:</strong> We'll call you at <strong>{formData.passengerPhone}</strong> to confirm your booking. Once your driver is assigned, you'll receive a text message with a link to track your ride in real-time.
              </p>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => setBookingConfirmed(false)}
                className="w-full bg-white text-gray-700 px-6 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 transition-all duration-300 border-2 border-gray-200"
              >
                Book Another Ride
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Book Nassau Airport Taxi | LPIA Transfer Booking | Taxi-98"
        description="Book your Nassau airport taxi online. Reserve LPIA airport transfers to Paradise Island, Atlantis, cruise port. Instant confirmation. Official government rates. Easy online booking."
        canonical="/book"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="bg-coral-100 text-coral-700 px-4 py-2 rounded-full text-sm font-semibold border border-coral-300 inline-block mb-4">
            Book Your Ride
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Book Your Nassau Airport Taxi
          </h1>
          <p className="text-xl text-gray-600">
            Reserve LPIA airport transfer to Paradise Island, Atlantis, and all Nassau destinations
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-cyan-100">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Passenger Information */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <User className="w-6 h-6 text-cyan-600 mr-3" />
                    Passenger Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label htmlFor="passengerName" className="block text-gray-700 font-medium mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="passengerName"
                        name="passengerName"
                        value={formData.passengerName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="passengerEmail" className="block text-gray-700 font-medium mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          id="passengerEmail"
                          name="passengerEmail"
                          value={formData.passengerEmail}
                          onChange={handleChange}
                          required
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                          placeholder="john@example.com"
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-2 italic">
                        Required for booking confirmation, tracking updates, and receipt delivery.
                      </p>
                    </div>

                    <div>
                      <label htmlFor="passengerPhone" className="block text-gray-700 font-medium mb-2">
                        Phone Number *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          id="passengerPhone"
                          name="passengerPhone"
                          value={formData.passengerPhone}
                          onChange={handleChange}
                          required
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                          placeholder="1-242-810-7335"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trip Details */}
                <div className="border-t border-cyan-100 pt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <MapPin className="w-6 h-6 text-cyan-600 mr-3" />
                    Trip Details
                  </h2>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="pickupZone" className="block text-gray-700 font-medium mb-2">
                          Pickup Zone *
                        </label>
                        <select
                          id="pickupZone"
                          name="pickupZone"
                          value={formData.pickupZone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                        >
                          {zones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zone.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="dropoffZone" className="block text-gray-700 font-medium mb-2">
                          Drop-off Zone *
                        </label>
                        <select
                          id="dropoffZone"
                          name="dropoffZone"
                          value={formData.dropoffZone}
                          onChange={handleChange}
                          required
                          data-testid="booking-dropoff-select"
                          aria-invalid={isSameZone || undefined}
                          className={`w-full px-4 py-3 bg-gray-50 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 transition-all duration-300 ${
                            isSameZone
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500/20'
                          }`}
                        >
                          <option value="">Select destination...</option>
                          {zones.filter(z => z.id !== formData.pickupZone).map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zone.name}
                            </option>
                          ))}
                        </select>
                        {isSameZone && (
                          <p
                            data-testid="booking-same-zone-warning"
                            className="mt-2 text-sm font-medium text-red-600 flex items-start gap-1.5"
                          >
                            <span aria-hidden="true">⚠</span>
                            <span>Please choose a different drop-off zone.</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Additional Stops */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-gray-700 font-medium flex items-center">
                          <MapPin className="w-5 h-5 text-blue-600 mr-2" />
                          Additional Stops (Optional)
                        </label>
                        {additionalStops.length < 3 && (
                          <button
                            type="button"
                            onClick={addStop}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
                          >
                            <Plus className="w-4 h-4" />
                            Add Stop
                          </button>
                        )}
                      </div>
                      
                      {additionalStops.length > 0 && (
                        <div className="space-y-3 mb-4">
                          {additionalStops.map((stop, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <div className="flex-1">
                                <select
                                  value={stop}
                                  onChange={(e) => updateStop(index, e.target.value)}
                                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                                >
                                  <option value="">Select stop {index + 1}...</option>
                                  {zones.filter(z => z.id !== formData.pickupZone && z.id !== formData.dropoffZone && !additionalStops.includes(z.id)).map((zone) => (
                                    <option key={zone.id} value={zone.id}>
                                      {zone.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeStop(index)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                aria-label="Remove stop"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {additionalStops.length === 0 && (
                        <p className="text-sm text-gray-500 mb-4">
                          Need to make stops along the way? Each additional stop has a <strong>$10 stop fee</strong>; waiting time is billed at <strong>$1.50 per minute</strong> ($90/hour) from the first minute. Add up to 3 additional stops.
                        </p>
                      )}
                      {additionalStops.length > 0 && (
                        <p className="text-sm text-blue-600 font-medium">
                          +${(additionalStops.filter(s => s).length * 10).toFixed(2)} stop fee for {additionalStops.filter(s => s).length} additional stop(s). Waiting time: $1.50/min from minute 1.
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="date" className="block text-gray-700 font-medium mb-2">
                          Date *
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="date"
                            id="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            required
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="time" className="block text-gray-700 font-medium mb-2">
                          Time *
                        </label>
                        <div className="relative">
                          <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="time"
                            id="time"
                            name="time"
                            value={formData.time}
                            onChange={handleChange}
                            required
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                          />
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Mon-Sat: 5AM-11PM, Sun: Pre-arranged</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label htmlFor="passengers" className="block text-gray-700 font-medium mb-2 text-sm">
                          Passengers
                        </label>
                        <input
                          type="number"
                          id="passengers"
                          name="passengers"
                          data-testid="passengers-input"
                          value={formData.passengers}
                          onChange={handleChange}
                          min="1"
                          max="7"
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                        />
                      </div>

                      <div>
                        <label htmlFor="childrenUnder3" className="block text-gray-700 font-medium mb-2 text-sm">
                          Kids Under 3
                        </label>
                        <input
                          type="number"
                          id="childrenUnder3"
                          name="childrenUnder3"
                          value={formData.childrenUnder3}
                          onChange={handleChange}
                          min="0"
                          max="3"
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                        />
                        <p className="text-xs text-green-600 mt-1">Free</p>
                      </div>

                      <div>
                        <label htmlFor="luggage" className="block text-gray-700 font-medium mb-2 text-sm">
                          Suitcases *
                        </label>
                        <input
                          type="number"
                          id="luggage"
                          name="luggage"
                          data-testid="booking-suitcases-input"
                          value={formData.luggage}
                          onChange={handleChange}
                          min="0"
                          max="20"
                          required
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                        />
                        <p className="text-xs text-coral-600 mt-1 font-medium">2 suitcases included. Additional luggage: $5 each.</p>
                      </div>

                      <div>
                        <label htmlFor="largeBags" className="block text-gray-700 font-medium mb-2 text-sm">
                          Oversized Items
                        </label>
                        <input
                          type="number"
                          id="largeBags"
                          name="largeBags"
                          data-testid="booking-oversized-input"
                          value={formData.largeBags}
                          onChange={handleChange}
                          min="0"
                          max="10"
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                        />
                        <p className="text-xs text-coral-600 mt-1 font-medium">Oversized or heavy items: $7 each — golf bags, boxes, large suitcases</p>
                      </div>
                    </div>

                    {/* Billable Add-ons Section */}
                    <div className="mt-8 pt-8 border-t border-cyan-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <DollarSign className="w-5 h-5 text-coral-600 mr-2" />
                        Billable Add-ons
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="carSeats" className="block text-gray-700 font-medium mb-2">
                            Car Seats
                          </label>
                          <input
                            type="number"
                            id="carSeats"
                            name="carSeats"
                            value={formData.carSeats}
                            onChange={handleChange}
                            min="0"
                            max="3"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                          />
                          <p className="text-xs text-coral-600 mt-1 font-medium">$10.00 per car seat (flat additional charge)</p>
                        </div>

                        <div>
                          <label htmlFor="waitingMinutes" className="block text-gray-700 font-medium mb-2">
                            Waiting Time (Minutes)
                          </label>
                          <input
                            type="number"
                            id="waitingMinutes"
                            name="waitingMinutes"
                            value={formData.waitingMinutes}
                            onChange={handleChange}
                            min="0"
                            max="480"
                            step="5"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                          />
                          <p className="text-xs text-coral-600 mt-1 font-medium">Charged from minute 1 at $1.50 per minute ($90/hour). No free minutes.</p>
                        </div>
                      </div>

                      {/* Shopping Plaza Stop — radio group (single-select prevents double-charge) */}
                      <div className="mt-8" data-testid="shopping-plaza-stop-section">
                        <h4 className="text-base font-bold text-gray-900 mb-2">Shopping Plaza Stop</h4>
                        <p className="text-xs text-gray-600 mb-3">
                          Grocery store and liquor store are located in the same shopping plaza. Select one option only.
                        </p>
                        <div className="space-y-2">
                          {Object.values(SHOPPING_STOP_OPTIONS).map((opt) => (
                            <label
                              key={opt.id}
                              data-testid={`shopping-stop-option-${opt.id}`}
                              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                                formData.shoppingStop === opt.id
                                  ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-500/20'
                                  : 'border-gray-300 bg-gray-50 hover:border-cyan-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="shoppingStop"
                                value={opt.id}
                                checked={formData.shoppingStop === opt.id}
                                onChange={handleChange}
                                className="mt-1 accent-cyan-600"
                              />
                              <span className="text-sm text-gray-800">{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="border-t border-cyan-100 pt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <DollarSign className="w-6 h-6 text-cyan-600 mr-3" />
                    Payment Method
                  </h2>
                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-xl p-6" data-testid="payment-options-block">
                    <div className="flex items-start space-x-3">
                      <DollarSign className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-gray-900 font-semibold text-lg mb-2">
                          Payment Options
                        </p>
                        <p className="text-gray-700 mb-3">
                          Taxi-98 accepts Visa, Mastercard, American Express, Discover, Apple Pay / Tap-to-Pay, PayPal and Cash. Payment can be completed at pickup or confirmed after booking.
                        </p>
                        <p className="text-sm text-gray-600">
                          Visa, Mastercard, American Express, Discover, Apple Pay / Tap-to-Pay, PayPal and Cash accepted.
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* 2026-06-04 — Free onboard Wi-Fi reassurance note (not a paid add-on, not part of fare) */}
                  <div
                    data-testid="booking-wifi-note"
                    className="mt-4 flex items-start gap-2 text-sm text-cyan-800 bg-cyan-50/60 border border-cyan-100 rounded-lg px-4 py-3"
                  >
                    <Wifi className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0" aria-hidden="true" />
                    <span>Free onboard Wi-Fi is available during your ride.</span>
                  </div>
                </div>


                {/* Flight Details Section */}
                <div className="border-t border-cyan-100 pt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <svg className="w-6 h-6 text-cyan-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Flight Details
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    If your trip involves airport pickup or drop-off, please provide your flight information.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="airline" className="block text-gray-700 font-medium mb-2">
                        Airline
                      </label>
                      <input
                        type="text"
                        id="airline"
                        name="airline"
                        value={formData.airline}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                        placeholder="e.g., American Airlines, Delta"
                      />
                    </div>

                    <div>
                      <label htmlFor="flightNumber" className="block text-gray-700 font-medium mb-2">
                        Flight Number
                      </label>
                      <input
                        type="text"
                        id="flightNumber"
                        name="flightNumber"
                        value={formData.flightNumber}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                        placeholder="e.g., AA1234"
                      />
                    </div>

                    <div>
                      <label htmlFor="departureTime" className="block text-gray-700 font-medium mb-2">
                        Departure Time
                      </label>
                      <input
                        type="time"
                        id="departureTime"
                        name="departureTime"
                        value={formData.departureTime}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                      />
                    </div>

                    <div>
                      <label htmlFor="arrivalTime" className="block text-gray-700 font-medium mb-2">
                        Arrival Time
                      </label>
                      <input
                        type="time"
                        id="arrivalTime"
                        name="arrivalTime"
                        value={formData.arrivalTime}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Special Requests - Notes Only */}
                <div className="border-t border-cyan-100 pt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <MessageSquare className="w-6 h-6 text-cyan-600 mr-3" />
                    Additional Notes (Optional)
                  </h2>
                  <p className="text-sm text-gray-600 mb-3">
                    Use this field for notes only (e.g., flight number, special instructions). For billable items like additional stops, car seats, or waiting time, use the fields above.
                  </p>
                  <textarea
                    id="specialRequests"
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300 resize-none"
                    placeholder="Example: Flight AA123 arriving at 2:30 PM, please meet at arrival gate"
                  ></textarea>
                </div>

                {/* Security Verification */}
                <div className="border-t border-cyan-100 pt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Shield className="w-6 h-6 text-cyan-600 mr-3" />
                    Security Verification
                  </h2>
                  <div className="flex flex-col items-center">
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={process.env.REACT_APP_HCAPTCHA_SITEKEY || '10000000-ffff-ffff-ffff-000000000001'}
                      onVerify={handleCaptchaVerify}
                      onExpire={handleCaptchaExpire}
                      onError={handleCaptchaError}
                      theme="light"
                      size="normal"
                    />
                    {captchaError && (
                      <p className="text-red-600 text-sm mt-2">{captchaError}</p>
                    )}
                    {captchaToken && (
                      <p className="text-green-600 text-sm mt-2 flex items-center">
                        <Check className="w-4 h-4 mr-1" /> Verified
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Error Banner */}
                {submitError && (
                  <div
                    data-testid="booking-submit-error"
                    className="border-l-4 border-red-500 bg-red-50 rounded-lg p-4 whitespace-pre-line"
                    role="alert"
                  >
                    <p className="font-semibold text-red-800 mb-1">We couldn't submit your booking</p>
                    <p className="text-red-700 text-sm">{submitError}</p>
                  </div>
                )}

                {/* Submit Button — disabled when same-zone, no captcha, or already submitting */}
                <button
                  type="submit"
                  data-testid="booking-submit-button"
                  disabled={!captchaToken || submitting || isSameZone}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-5 rounded-lg font-bold text-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {submitting ? 'Submitting…' : 'Confirm Booking'}
                </button>

                {/* Payment Information */}
                <div className="text-center pt-4">
                  <p className="text-gray-700 font-medium text-base">
                    Visa, Mastercard, American Express, Discover, Apple Pay / Tap-to-Pay, PayPal and Cash accepted.
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Payment can be completed at pickup or confirmed after booking.
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* Fare Estimate Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-8 rounded-3xl shadow-xl text-white sticky top-32">
              <div className="flex items-center space-x-3 mb-6">
                <DollarSign className="w-8 h-8" />
                <h2 className="text-2xl font-bold">Fare Estimate</h2>
              </div>

              {/* Same-zone guard rendered ABOVE fareEstimate so the customer
                  never sees a number while pickup === dropoff (LB-3) */}
              {isSameZone ? (
                <div
                  data-testid="booking-fare-same-zone-block"
                  className="bg-red-500/15 border border-red-300/40 backdrop-blur-sm p-5 rounded-xl"
                >
                  <p className="text-white font-semibold text-base mb-1">
                    Different drop-off required
                  </p>
                  <p className="text-red-50/90 text-sm leading-relaxed">
                    Please choose a different drop-off zone. We can&rsquo;t calculate a fare when the pickup and drop-off are the same.
                  </p>
                </div>
              ) : fareEstimate ? (
                <div className="space-y-4">
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                    <p className="text-cyan-100 text-sm mb-1">Base Fare</p>
                    <p className="text-3xl font-bold">${fareEstimate.baseFare.toFixed(2)}</p>
                  </div>

                  {fareEstimate.extraPassengers > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Extra Passengers</span>
                      <span className="font-semibold">${fareEstimate.extraPassengers.toFixed(2)}</span>
                    </div>
                  )}

                  {fareEstimate.extraLuggage > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Extra Luggage</span>
                      <span className="font-semibold">${fareEstimate.extraLuggage.toFixed(2)}</span>
                    </div>
                  )}

                  {fareEstimate.largeBags > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Large Bags/Boxes</span>
                      <span className="font-semibold">${fareEstimate.largeBags.toFixed(2)}</span>
                    </div>
                  )}

                  {fareEstimate.bridgeToll > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20 bg-yellow-500/20 px-2 rounded">
                      <span className="text-yellow-100 font-medium">Paradise Island Toll</span>
                      <span className="font-semibold text-yellow-100">${fareEstimate.bridgeToll.toFixed(2)}</span>
                    </div>
                  )}

                  {fareEstimate.carSeats > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Car Seats</span>
                      <span className="font-semibold text-coral-200">${fareEstimate.carSeats.toFixed(2)}</span>
                    </div>
                  )}

                  {fareEstimate.waitingTime > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Waiting Time</span>
                      <span className="font-semibold text-coral-200">${fareEstimate.waitingTime.toFixed(2)}</span>
                    </div>
                  )}

                  {fareEstimate.shoppingStop > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20" data-testid="booking-shopping-stop-line">
                      <span className="text-cyan-100">
                        Shopping Plaza Stop
                        <span className="block text-xs text-cyan-200/80">
                          {SHOPPING_STOP_OPTIONS[fareEstimate.shoppingStopOption]?.display || ''}
                        </span>
                      </span>
                      <span className="font-semibold">${fareEstimate.shoppingStop.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl mt-6">
                    <p className="text-cyan-100 text-sm mb-2">Total Estimated Fare</p>
                    <p className="text-4xl font-bold" data-testid="booking-fare-total">${fareEstimate.total.toFixed(2)}</p>
                    <p className="text-cyan-100 text-xs italic mt-3" data-testid="booking-fuel-adjustment-note">
                      <strong className="not-italic">Fare Update Notice:</strong> Due to increased fuel costs in The Bahamas, Taxi-98 fares have been updated. Your estimated fare now reflects this adjustment, while keeping pricing simple, clear, and upfront.
                    </p>
                  </div>

                  <p className="text-cyan-100 text-xs text-center mt-4">
                    Official government rates
                  </p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-cyan-100">
                    Select pickup and drop-off zones to see fare estimate
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default BookingForm;
